import { Instance } from "mobx-state-tree";
import { SurveyRuleConditionModel } from "../../../models/SurveyRuleConditionModel";
import { TFunction } from "i18next";
import { SurveyRuleAction } from "@pulse/pulse-rpcs/lib/survey-rules/surveyRuleAction";
import { SurveyRuleModel } from "../../../models/SurveyRuleModel";

type RuleKeyType = "QUESTION" | "RESPONDENT" | "MIXED";

const TARGET_VALUE_SEPARATOR = "_";

const getRuleKeyType = (
  conditions: Instance<typeof SurveyRuleConditionModel>[],
): RuleKeyType => {
  const hasQuestionCondition = conditions.some(
    (condition) => condition.keyType.type === "QUESTION",
  );
  const hasRespondentCondition = conditions.some(
    (condition) => condition.keyType.type === "RESPONDENT",
  );

  if (hasQuestionCondition && hasRespondentCondition) {
    return "MIXED";
  }
  return hasRespondentCondition ? "RESPONDENT" : "QUESTION";
};

export const getRuleTitle = (
  conditions: Instance<typeof SurveyRuleConditionModel>[],
  t: TFunction,
): string => {
  const ruleKeyType = getRuleKeyType(conditions);
  const isGroupRule = conditions.length > 1;

  if (isGroupRule) {
    switch (ruleKeyType) {
      case "QUESTION":
        return t("surveys.rules.questionGroupRule");
      case "RESPONDENT":
        return t("surveys.rules.respondentGroupRule");
      case "MIXED":
        return t("surveys.rules.mixedGroupRule");
      default:
        ruleKeyType satisfies never;
    }
  }

  switch (ruleKeyType) {
    case "QUESTION":
      return t("surveys.rules.questionSingleRule");
    case "RESPONDENT":
      return t("surveys.rules.respondentSingleRule");
    case "MIXED":
      return t("surveys.rules.singleRule");
    default:
      return ruleKeyType satisfies never;
  }
};

export const getConditionKey = (
  condition: Instance<typeof SurveyRuleConditionModel>,
  t: TFunction,
): string => condition.keyType.conditionKey ?? t("surveys.rules.sourceKey");

export const getConditionValue = (
  condition: Instance<typeof SurveyRuleConditionModel>,
  t: TFunction,
): string =>
  condition.keyType.conditionValue.length
    ? condition.keyType.conditionValue.join(", ")
    : t("surveys.rules.sourceValue");

export const getActionLabel = (
  action: SurveyRuleAction.SurveyRuleAction | null,
  t: TFunction,
): string => {
  switch (action) {
    case SurveyRuleAction.SurveyRuleAction.SHOW_TARGET:
      return t("surveys.rules.showTargetAction");
    case SurveyRuleAction.SurveyRuleAction.HIDE_TARGET:
      return t("surveys.rules.hideTargetAction");
    case SurveyRuleAction.SurveyRuleAction.SKIP_TO:
      return t("surveys.rules.skipToAction");
    case null:
      return t("surveys.rules.action");
    default:
      return action satisfies never;
  }
};

export const getTargetLabel = (
  surveyRule: Instance<typeof SurveyRuleModel>,
  t: TFunction,
): string =>
  surveyRule.target.length
    ? surveyRule.target
        .map((target) =>
          target.gridRowCode
            ? `${target.questionCode}${TARGET_VALUE_SEPARATOR}${target.gridRowCode}`
            : target.questionCode,
        )
        .join(", ")
    : t("surveys.rules.targetKey");
